import { ITag } from '@/types'

// 작품 필터 (/artworks?tags=1,8&colors=RED,NAVY&sizes=XS,S&minPrice=100000&maxPrice=500000).
// api: GET /artworks/shop (ShopFilterDto). 한 항목 안은 OR, 항목끼리는 AND.

export const COLORS = [
  ['RED', '빨강', '#E53935'],
  ['ORANGE', '주황', '#FB8C00'],
  ['YELLOW', '노랑', '#FDD835'],
  ['BEIGE', '베이지', '#E8DCC4'],
  ['GREEN', '초록', '#43A047'],
  ['BLUE', '파랑', '#1E88E5'],
  ['NAVY', '남색', '#1A237E'],
  ['PINK', '분홍', '#F48FB1'],
  ['PURPLE', '보라', '#8E24AA'],
  ['BROWN', '갈색', '#795548'],
  ['WHITE', '흰색', '#FFFFFF'],
  ['GRAY', '회색', '#9E9E9E'],
  ['BLACK', '검정', '#212121'],
] as const
export type ColorKey = (typeof COLORS)[number][0]
// light swatches get a dark check mark
export const LIGHT_COLORS: ColorKey[] = ['YELLOW', 'BEIGE', 'WHITE']

// 크기 등급 (A 규격, api common/enums/size.ts). paper = 종이 비율 그림의 높이 (A4 = 1)
export const SIZES = [
  ['XS', 'A4 이하', 1],
  ['S', 'A3 이하', 1.41],
  ['M', 'A2 이하', 2],
  ['L', 'A1 이하', 2.83],
  ['XL', 'A1 초과', 4],
] as const
export type SizeKey = (typeof SIZES)[number][0]

// 가로 · 세로 (mm) → 크기 등급. api helpers/determin-size.ts 와 같은 규칙 (짧은 변 · 긴 변이 모두 그 규격 안)
const SIZE_LIMITS: [SizeKey, number, number][] = [
  ['XS', 210, 297],
  ['S', 297, 420],
  ['M', 420, 594],
  ['L', 594, 841],
]
export const sizeOf = (width: number, height: number): SizeKey => {
  const short = Math.min(width, height)
  const long = Math.max(width, height)
  return SIZE_LIMITS.find(([, s, l]) => short <= s && long <= l)?.[0] ?? 'XL'
}

export interface ShopFilter {
  tags: number[]
  colors: ColorKey[]
  sizes: SizeKey[]
  minPrice?: number
  maxPrice?: number
}

export const EMPTY_FILTER: ShopFilter = { tags: [], colors: [], sizes: [] }

const COLOR_KEYS = COLORS.map(([k]) => k) as string[]
const SIZE_KEYS = SIZES.map(([k]) => k) as string[]

const list = (v: unknown) =>
  String(v ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
const price = (v: unknown) => {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined
}

// URL search → filter (unknown values dropped)
export const parseFilter = (search: Record<string, unknown>): ShopFilter => ({
  tags: list(search.tags)
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0),
  colors: list(search.colors).filter((c) => COLOR_KEYS.includes(c)) as ColorKey[],
  sizes: list(search.sizes).filter((s) => SIZE_KEYS.includes(s)) as SizeKey[],
  minPrice: price(search.minPrice),
  maxPrice: price(search.maxPrice),
})

// filter → URL search / api params (empty keys left out)
export const filterParams = (f: ShopFilter) => ({
  ...(f.tags.length ? { tags: f.tags.join(',') } : {}),
  ...(f.colors.length ? { colors: f.colors.join(',') } : {}),
  ...(f.sizes.length ? { sizes: f.sizes.join(',') } : {}),
  ...(f.minPrice ? { minPrice: String(f.minPrice) } : {}),
  ...(f.maxPrice ? { maxPrice: String(f.maxPrice) } : {}),
})

// add or remove one value
export const toggle = <T>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v])

export const activeCount = (f: ShopFilter) =>
  f.tags.length + f.colors.length + f.sizes.length + (f.minPrice || f.maxPrice ? 1 : 0)

//---------------------------------------------------------------------------
// tags tree: a picked parent id stands for all of its children

export const tagState = (tree: ITag[], selected: number[], tag: ITag): boolean | 'indeterminate' => {
  if (selected.includes(tag.id)) return true
  const parent = tree.find((p) => p.children?.some((c) => c.id === tag.id))
  if (parent && selected.includes(parent.id)) return true
  if (tag.children?.some((c) => selected.includes(c.id))) return 'indeterminate'
  return false
}

export const toggleTag = (tree: ITag[], selected: number[], tag: ITag): number[] => {
  const parent = tree.find((p) => p.children?.some((c) => c.id === tag.id))
  // parent row: all or nothing
  if (!parent) {
    const childIds = (tag.children ?? []).map((c) => c.id)
    const rest = selected.filter((id) => id !== tag.id && !childIds.includes(id))
    return tagState(tree, selected, tag) === true ? rest : [...rest, tag.id]
  }
  const siblings = (parent.children ?? []).map((c) => c.id)
  // child of a fully picked parent → the parent becomes its other children
  if (selected.includes(parent.id)) {
    return [...selected.filter((id) => id !== parent.id), ...siblings.filter((id) => id !== tag.id)]
  }
  const next = selected.includes(tag.id) ? selected.filter((id) => id !== tag.id) : [...selected, tag.id]
  // every child picked → collapse into the parent
  return siblings.every((id) => next.includes(id)) ? [...next.filter((id) => !siblings.includes(id)), parent.id] : next
}

export const tagName = (tree: ITag[], id: number) => {
  for (const p of tree) {
    if (p.id === id) return p.name
    const c = p.children?.find((c) => c.id === id)
    if (c) return `${p.name} · ${c.name}`
  }
  return String(id)
}

//---------------------------------------------------------------------------
// price: slider position 0…STEPS on a log scale between the facets' min and max

export const PRICE_STEPS = 1000

export const priceAt = (pos: number, min: number, max: number) => {
  const raw = min * Math.pow(max / min, pos / PRICE_STEPS)
  // two significant digits: 12만, 350만, 1,200만
  const unit = Math.pow(10, Math.max(0, Math.floor(Math.log10(raw)) - 1))
  return Math.round(raw / unit) * unit
}

export const posOf = (price: number | undefined, min: number, max: number, fallback: number) =>
  price ? Math.round((Math.log(price / min) / Math.log(max / min)) * PRICE_STEPS) : fallback

// 35만원, 1,200만원
export const won = (n: number) =>
  n >= 10_000 ? `${Math.round(n / 10_000).toLocaleString('ko-KR')}만원` : `${n.toLocaleString('ko-KR')}원`

export const priceLabel = (f: Pick<ShopFilter, 'minPrice' | 'maxPrice'>) =>
  f.minPrice && f.maxPrice
    ? `${won(f.minPrice)} ~ ${won(f.maxPrice)}`
    : f.minPrice
      ? `${won(f.minPrice)} 이상`
      : f.maxPrice
        ? `${won(f.maxPrice)} 이하`
        : ''
